import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Heart, AlertTriangle } from "lucide-react";

interface HealthScoreBreakdownProps {
  delivery: number;
  engagement: number;
  billing: number;
  contract: number;
  riskFactors: string[];
}

function progressColor(score: number) {
  if (score >= 70) return "[&>div]:bg-green-500";
  if (score >= 50) return "[&>div]:bg-yellow-500";
  if (score >= 30) return "[&>div]:bg-orange-500";
  return "[&>div]:bg-red-500";
}

const components: { label: string; key: keyof Omit<HealthScoreBreakdownProps, "riskFactors"> }[] = [
  { label: "Delivery", key: "delivery" },
  { label: "Engagement", key: "engagement" },
  { label: "Billing", key: "billing" },
  { label: "Contract", key: "contract" },
];

export default function HealthScoreBreakdown(props: HealthScoreBreakdownProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <Heart className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-base">Health Score Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {components.map(({ label, key }) => {
          const score = props[key];
          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>{label}</span>
                <span className="text-muted-foreground">{score}/100</span>
              </div>
              <Progress value={score} className={progressColor(score)} />
            </div>
          );
        })}

        {props.riskFactors.length > 0 && (
          <div className="pt-2 space-y-2">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span>Risk Factors</span>
            </div>
            <ul className="space-y-1">
              {props.riskFactors.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
